import type { SandboxProfile } from './types'

export const b2bCrmProfile: SandboxProfile = {
  id: 'b2b-crm',
  label: 'B2B CRM',
  brand: {
    domain: 'acme-crm.io',
    name: 'Acme CRM',
    isYou: true,
  },
  industry: 'B2B SaaS — CRM software',
  prompts: [
    {
      id: 'p1',
      text: 'best B2B CRM software',
      status: {
        chatgpt: 'partial',
        perplexity: 'partial',
        google_aio: 'not-visible',
      },
      visibilityScore: 28,
      trend: 5,
      lastScanned: '2h ago',
      responses: {
        chatgpt: {
          text: 'For B2B teams, <competitor id="salesforce">Salesforce</competitor> remains the enterprise standard with deep customization and a vast partner ecosystem. <competitor id="hubspot">HubSpot</competitor> is popular among mid-market teams due to its free tier and marketing integrations. <brand id="acme">Acme CRM</brand> is gaining traction among growth-stage startups for its transparent pricing and developer-friendly API. <competitor id="pipedrive">Pipedrive</competitor> suits sales-focused teams that want a clean pipeline view without heavy configuration overhead.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['g2.com', 'trustradius.com'],
        },
        perplexity: {
          text: 'The B2B CRM landscape in 2026 is led by <competitor id="salesforce">Salesforce</competitor>, which commands over a third of enterprise deals. <competitor id="hubspot">HubSpot</competitor> has expanded into sales and service hubs, making it a strong all-in-one option. <brand id="acme">Acme CRM</brand> stands out for SMBs with its flat-rate pricing model and Slack-native notifications. Independent review platforms like G2 and Capterra rate it highly for onboarding speed.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['g2.com', 'capterra.com'],
        },
        google_aio: {
          text: 'Top B2B CRM solutions include <competitor id="salesforce">Salesforce</competitor> for enterprise teams and <competitor id="hubspot">HubSpot</competitor> for growing businesses. <competitor id="pipedrive">Pipedrive</competitor> is a strong option for sales-focused teams.',
          cited: [],
          citedSources: ['capterra.com'],
        },
      },
    },
    {
      id: 'p2',
      text: 'best CRM for startups',
      status: {
        chatgpt: 'visible',
        perplexity: 'visible',
        google_aio: 'not-visible',
      },
      visibilityScore: 62,
      trend: 12,
      lastScanned: '2h ago',
      responses: {
        chatgpt: {
          text: 'Startups benefit most from CRMs that scale without locking in long-term contracts. <brand id="acme">Acme CRM</brand> is frequently recommended for early-stage teams — it offers a generous free tier, a public API with SDKs in five languages, and per-seat pricing that stays predictable as you grow. <competitor id="hubspot">HubSpot</competitor> is a common alternative due to its marketing automation, though its pricing jumps sharply at higher tiers. Both are cited on G2 as top-rated startup CRMs.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['g2.com', 'trustradius.com', 'acme-crm.io/blog'],
        },
        perplexity: {
          text: '<brand id="acme">Acme CRM</brand> and <competitor id="hubspot">HubSpot</competitor> are consistently highlighted as best-fit CRMs for startups in 2026. Acme CRM\'s developer API and Slack integration make it popular among technical co-founders, while HubSpot appeals to marketing-led growth teams. Reddit\'s r/CRM community frequently recommends Acme for its transparent pricing and no-surprise billing.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['reddit.com/r/CRM', 'g2.com', 'acme-crm.io/blog'],
        },
        google_aio: {
          text: '<competitor id="hubspot">HubSpot</competitor> and <competitor id="pipedrive">Pipedrive</competitor> are popular CRM choices for startups. Both offer free tiers and easy onboarding.',
          cited: [],
          citedSources: ['g2.com'],
        },
      },
    },
    {
      id: 'p3',
      text: 'CRM with Slack integration',
      status: {
        chatgpt: 'visible',
        perplexity: 'visible',
        google_aio: 'partial',
      },
      visibilityScore: 78,
      trend: 8,
      lastScanned: '2h ago',
      responses: {
        chatgpt: {
          text: '<brand id="acme">Acme CRM</brand> is the most-cited CRM for native Slack integration. Unlike <competitor id="salesforce">Salesforce</competitor>\'s Slack acquisition (which is bundled into expensive enterprise tiers), Acme CRM ships Slack notifications, deal alerts, and pipeline updates natively at all pricing tiers. G2 reviewers highlight this as a key differentiator. <competitor id="hubspot">HubSpot</competitor> also offers a Slack bot but requires a paid Operations Hub add-on.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['g2.com', 'trustradius.com'],
        },
        perplexity: {
          text: 'If Slack is central to your workflow, <brand id="acme">Acme CRM</brand> is the standout choice. Its integration goes beyond simple notifications — you can update deal stages, log calls, and assign tasks directly from Slack channels. <competitor id="pipedrive">Pipedrive</competitor> offers a lighter Slack integration via Zapier, while <competitor id="salesforce">Salesforce</competitor>\'s deep Slack features require Salesforce+ Enterprise licensing.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['g2.com', 'reddit.com/r/CRM'],
        },
        google_aio: {
          text: 'Several CRMs offer Slack integrations. <brand id="acme">Acme CRM</brand> provides native Slack notifications. <competitor id="salesforce">Salesforce</competitor> and <competitor id="hubspot">HubSpot</competitor> also integrate with Slack via their respective app directories.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['g2.com'],
        },
      },
    },
    {
      id: 'p4',
      text: 'alternative to Salesforce',
      status: {
        chatgpt: 'partial',
        perplexity: 'not-visible',
        google_aio: 'not-visible',
      },
      visibilityScore: 14,
      trend: -3,
      lastScanned: '2h ago',
      responses: {
        chatgpt: {
          text: 'Teams looking to move off <competitor id="salesforce">Salesforce</competitor> often evaluate <competitor id="hubspot">HubSpot</competitor> (strong marketing automation), <competitor id="pipedrive">Pipedrive</competitor> (sales-focused simplicity), and <competitor id="zoho">Zoho CRM</competitor> (broad feature set at lower cost). <brand id="acme">Acme CRM</brand> is emerging as an option for developer-led teams that need API flexibility without Salesforce\'s configuration overhead. Migration tooling and data portability are key considerations for any switch.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['trustradius.com', 'g2.com'],
        },
        perplexity: {
          text: 'The top Salesforce alternatives in 2026 are <competitor id="hubspot">HubSpot</competitor>, <competitor id="pipedrive">Pipedrive</competitor>, and <competitor id="zoho">Zoho CRM</competitor>. Each offers lower total cost of ownership for SMB and mid-market segments. Enterprise teams should also evaluate Microsoft Dynamics and Oracle CX, which offer comparable customization depth.',
          cited: [],
          citedSources: ['g2.com', 'trustradius.com'],
        },
        google_aio: {
          text: 'Popular alternatives to <competitor id="salesforce">Salesforce</competitor> include <competitor id="hubspot">HubSpot</competitor> and <competitor id="pipedrive">Pipedrive</competitor>, both of which offer simpler pricing and easier onboarding.',
          cited: [],
          citedSources: ['capterra.com'],
        },
      },
    },
    {
      id: 'p5',
      text: 'best CRM with transparent pricing',
      status: {
        chatgpt: 'visible',
        perplexity: 'partial',
        google_aio: 'not-visible',
      },
      visibilityScore: 55,
      trend: 18,
      lastScanned: '2h ago',
      responses: {
        chatgpt: {
          text: 'Pricing transparency is a growing concern in CRM software. <brand id="acme">Acme CRM</brand> is consistently cited for its flat per-seat pricing with no hidden module fees — every plan includes the full feature set, differentiated only by usage limits. <competitor id="pipedrive">Pipedrive</competitor> also offers clear pricing, though advanced automations require higher tiers. <competitor id="salesforce">Salesforce</competitor> is frequently criticized on G2 and TrustRadius for opaque add-on pricing and annual contract requirements.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['g2.com', 'trustradius.com', 'acme-crm.io/pricing'],
        },
        perplexity: {
          text: 'If pricing predictability matters, <competitor id="pipedrive">Pipedrive</competitor> and <brand id="acme">Acme CRM</brand> are the most-recommended options. Acme CRM\'s pricing page is frequently cited on HackerNews threads about SaaS cost control. However, Acme\'s website still lists a 14-day free trial in some places while the app shows 7 days — this inconsistency has appeared in multiple forum discussions.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['news.ycombinator.com', 'reddit.com/r/CRM'],
        },
        google_aio: {
          text: 'CRMs with transparent pricing include <competitor id="pipedrive">Pipedrive</competitor> and <competitor id="hubspot">HubSpot</competitor>. Both publish clear per-seat pricing on their websites.',
          cited: [],
          citedSources: ['capterra.com'],
        },
      },
    },
    {
      id: 'p6',
      text: 'best CRM API for developers',
      status: {
        chatgpt: 'partial',
        perplexity: 'partial',
        google_aio: 'not-visible',
      },
      visibilityScore: 32,
      trend: 4,
      lastScanned: '2h ago',
      responses: {
        chatgpt: {
          text: 'Developer experience is a key differentiator among CRM APIs. <competitor id="salesforce">Salesforce</competitor>\'s REST and Bulk APIs are the most mature but come with steep learning curves. <brand id="acme">Acme CRM</brand>\'s API is developer-focused — it ships with OpenAPI specs, Postman collections, and client libraries in Python, Node, Ruby, Go, and PHP. On HackerNews, Acme CRM is mentioned positively for its rate limits and webhook reliability, though its documentation could be more comprehensive than <competitor id="salesforce">Salesforce</competitor>\'s.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['news.ycombinator.com', 'g2.com'],
        },
        perplexity: {
          text: 'For developers building on top of a CRM, <competitor id="salesforce">Salesforce</competitor> offers the broadest ecosystem but a complex learning curve. <brand id="acme">Acme CRM</brand> is often preferred for greenfield integrations due to its clean REST API and predictable pagination. <competitor id="hubspot">HubSpot</competitor>\'s API is also popular, especially for marketing-adjacent use cases. A comparison table of API capabilities would help teams make informed decisions.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['news.ycombinator.com', 'acme-crm.io/features/api'],
        },
        google_aio: {
          text: '<competitor id="salesforce">Salesforce</competitor> and <competitor id="hubspot">HubSpot</competitor> provide widely-used CRM APIs with strong developer documentation and ecosystem support.',
          cited: [],
          citedSources: ['capterra.com'],
        },
      },
    },
    {
      id: 'p7',
      text: 'CRM with free trial',
      status: {
        chatgpt: 'partial',
        perplexity: 'visible',
        google_aio: 'not-visible',
      },
      visibilityScore: 44,
      trend: -2,
      lastScanned: '2h ago',
      responses: {
        chatgpt: {
          text: 'Most modern CRMs offer free trials, but the duration and feature access vary significantly. <brand id="acme">Acme CRM</brand> offers a 14-day free trial with full feature access and no credit card required. <competitor id="hubspot">HubSpot</competitor> has a free tier (unlimited duration) but caps contacts and lacks automation. <competitor id="pipedrive">Pipedrive</competitor> and <competitor id="salesforce">Salesforce</competitor> both offer 14-day trials.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['g2.com', 'capterra.com'],
        },
        perplexity: {
          text: '<brand id="acme">Acme CRM</brand> is frequently cited for having a no-friction free trial — no credit card, full feature access for 14 days. Review platforms like G2 and Capterra list it among the easiest CRMs to evaluate. <competitor id="hubspot">HubSpot</competitor>\'s free tier is popular but limited, while <competitor id="pipedrive">Pipedrive</competitor> and <competitor id="zoho">Zoho CRM</competitor> offer comparable trial experiences.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['g2.com', 'capterra.com', 'trustradius.com'],
        },
        google_aio: {
          text: '<competitor id="hubspot">HubSpot</competitor> offers a permanently free CRM tier. <competitor id="pipedrive">Pipedrive</competitor> and <competitor id="salesforce">Salesforce</competitor> both provide 14-day free trials.',
          cited: [],
          citedSources: ['capterra.com'],
        },
      },
    },
    {
      id: 'p8',
      text: 'affordable CRM for SMBs',
      status: {
        chatgpt: 'visible',
        perplexity: 'visible',
        google_aio: 'not-visible',
      },
      visibilityScore: 58,
      trend: 9,
      lastScanned: '2h ago',
      responses: {
        chatgpt: {
          text: 'SMBs consistently cite pricing and ease of setup as their top CRM criteria. <brand id="acme">Acme CRM</brand> starts at $19/seat/month with no feature tiering — every user gets the full platform. <competitor id="zoho">Zoho CRM</competitor> offers a similarly affordable entry point with broader feature coverage. <competitor id="pipedrive">Pipedrive</competitor> is slightly pricier but well-regarded for sales teams. <competitor id="hubspot">HubSpot</competitor>\'s free plan is popular for very early-stage SMBs but paid tiers quickly exceed SMB budgets.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['g2.com', 'capterra.com', 'trustradius.com'],
        },
        perplexity: {
          text: 'For budget-conscious SMBs, the top picks are <brand id="acme">Acme CRM</brand>, <competitor id="zoho">Zoho CRM</competitor>, and <competitor id="pipedrive">Pipedrive</competitor>. Acme CRM is particularly well-reviewed on Reddit\'s r/CRM for transparent, no-surprise billing. G2 and Capterra reviewers frequently mention Acme\'s customer support quality as a differentiator for smaller teams without dedicated IT staff.',
          cited: [{ domain: 'acme-crm.io', name: 'Acme CRM', isYou: true }],
          citedSources: ['reddit.com/r/CRM', 'g2.com', 'capterra.com'],
        },
        google_aio: {
          text: 'Affordable CRM options for SMBs include <competitor id="zoho">Zoho CRM</competitor>, <competitor id="pipedrive">Pipedrive</competitor>, and <competitor id="hubspot">HubSpot</competitor>\'s free tier.',
          cited: [],
          citedSources: ['capterra.com'],
        },
      },
    },
  ],
  competitors: [
    {
      domain: 'salesforce.com',
      name: 'Salesforce',
      shareOfVoice: 32,
      perEngine: { chatgpt: 36, perplexity: 36, google_aio: 28 },
      promptsWon: ['p1', 'p4', 'p6'],
    },
    {
      domain: 'hubspot.com',
      name: 'HubSpot',
      shareOfVoice: 18,
      perEngine: { chatgpt: 14, perplexity: 16, google_aio: 26 },
      promptsWon: ['p2', 'p4', 'p7'],
    },
    {
      domain: 'pipedrive.com',
      name: 'Pipedrive',
      shareOfVoice: 8,
      perEngine: { chatgpt: 7, perplexity: 8, google_aio: 10 },
      promptsWon: ['p5', 'p8'],
    },
    {
      domain: 'zoho.com',
      name: 'Zoho CRM',
      shareOfVoice: 6,
      perEngine: { chatgpt: 5, perplexity: 5, google_aio: 7 },
      promptsWon: ['p4', 'p8'],
    },
    {
      domain: 'monday.com',
      name: 'Monday CRM',
      shareOfVoice: 4,
      perEngine: { chatgpt: 3, perplexity: 2, google_aio: 4 },
      promptsWon: [],
    },
  ],
  suggestions: [
    {
      id: 's1',
      title: 'Get listed on g2.com “Best CRM Tools” — cited by all 3 engines',
      shortTitle: 'Get listed on g2.com “Best CRM Tools”',
      targetKind: 'source',
      grade: 'measured',
      sourceDomain: 'g2.com',
      reason:
        'g2.com is cited in answers to {n} of your buyer questions; HubSpot and Pipedrive are listed there, you are not. Claim your G2 profile and fill every category field the listicle filters on.',
    },
    {
      id: 's2',
      title: 'Unblock AI crawlers on your site',
      shortTitle: 'Unblock AI crawlers',
      targetKind: 'site',
      grade: 'measured',
      reason:
        'Your robots.txt blocks the crawlers two of the three engines use, so they cannot read your pages at all. Allow them, then check the file is live.',
      task: 'live',
    },
    {
      id: 's3',
      title: 'Create the missing page: Acme CRM vs Pipedrive',
      shortTitle: 'Create the missing page: Acme CRM vs Pipedrive',
      targetKind: 'site',
      grade: 'hypothesis',
      promptIds: ['p1', 'p4', 'p5', 'p8'],
      reason:
        '{n} of your buyer questions name Pipedrive as a rival, and no page of yours answers them. A comparison page is the likeliest place for an engine to name you. Unproven until a scan shows it.',
    },
    {
      id: 's4',
      title: 'Join the r/CRM thread on CRM for small teams',
      shortTitle: 'Join the r/CRM thread on CRM for small teams',
      targetKind: 'source',
      grade: 'measured',
      sourceDomain: 'reddit.com/r/CRM',
      reason:
        'This thread is cited in answers to {n} of your buyer questions; Zoho and HubSpot are recommended in it, you are not. Answer what the thread asks, as a person, with the details a buyer needs.',
    },
    {
      id: 's5',
      title: 'Add FAQ markup to /pricing',
      shortTitle: 'Add FAQ markup to /pricing',
      targetKind: 'site',
      grade: 'hypothesis',
      reason:
        'Your pricing answers sit in plain paragraphs. Marking them up as questions and answers makes them easy for an engine to lift.',
      task: 'live',
    },
    {
      id: 's6',
      title: 'Correct the free-trial length on /pricing',
      shortTitle: 'Correct the free-trial length on /pricing',
      targetKind: 'site',
      grade: 'measured',
      promptIds: ['p7'],
      reason:
        'ChatGPT and Perplexity both say your trial is 14 days; /pricing says 7. Make the page and the claim agree.',
    },
    {
      id: 's7',
      title: 'Get listed on capterra.com “Best CRM Software”',
      shortTitle: 'Get listed on capterra.com “Best CRM Software”',
      targetKind: 'source',
      grade: 'measured',
      sourceDomain: 'capterra.com',
      reason:
        'capterra.com is cited in answers to {n} of your buyer questions; Pipedrive and Zoho are listed there, you are not.',
    },
    {
      id: 's8',
      title: 'Publish a reviews page on acme-crm.io',
      shortTitle: 'Publish a reviews page on acme-crm.io',
      targetKind: 'site',
      grade: 'hypothesis',
      promptIds: ['p1', 'p2', 'p8'],
      reason:
        'You have no page that collects your third-party reviews in one place. Unproven until a scan shows engines reading it.',
    },
  ],
  sources: [
    {
      domain: 'g2.com',
      citationCount: 32,
      sharePercent: 24,
      promptsCitingIds: ['p1', 'p2', 'p3', 'p5', 'p6', 'p7', 'p8'],
    },
    {
      domain: 'trustradius.com',
      citationCount: 18,
      sharePercent: 14,
      promptsCitingIds: ['p1', 'p2', 'p4', 'p5', 'p6'],
    },
    {
      domain: 'capterra.com',
      citationCount: 16,
      sharePercent: 12,
      promptsCitingIds: ['p1', 'p4', 'p6', 'p7', 'p8'],
    },
    {
      domain: 'reddit.com/r/CRM',
      citationCount: 12,
      sharePercent: 9,
      promptsCitingIds: ['p2', 'p5', 'p8'],
    },
    {
      domain: 'news.ycombinator.com',
      citationCount: 8,
      sharePercent: 6,
      promptsCitingIds: ['p5', 'p6'],
    },
    {
      domain: 'nytimes.com',
      citationCount: 6,
      sharePercent: 5,
      promptsCitingIds: ['p1', 'p2'],
    },
    {
      domain: 'acme-crm.io/blog',
      citationCount: 4,
      sharePercent: 3,
      promptsCitingIds: ['p2', 'p6'],
    },
    {
      domain: 'en.wikipedia.org',
      citationCount: 3,
      sharePercent: 2,
      promptsCitingIds: ['p1', 'p4'],
    },
  ],
  visibility: [
    {
      date: '2026-01-10',
      score: 14,
      perEngine: { chatgpt: 16, perplexity: 14, google_aio: 12 },
    },
    {
      date: '2026-01-17',
      score: 15,
      perEngine: { chatgpt: 17, perplexity: 15, google_aio: 12 },
    },
    {
      date: '2026-01-24',
      score: 16,
      perEngine: { chatgpt: 18, perplexity: 16, google_aio: 13 },
      annotation: 'Task live: Unblock AI crawlers',
    },
    {
      date: '2026-01-31',
      score: 19,
      perEngine: { chatgpt: 22, perplexity: 20, google_aio: 15 },
    },
    {
      date: '2026-02-07',
      score: 21,
      perEngine: { chatgpt: 24, perplexity: 22, google_aio: 17 },
    },
    {
      date: '2026-02-14',
      score: 23,
      perEngine: { chatgpt: 26, perplexity: 24, google_aio: 18 },
    },
    {
      date: '2026-02-21',
      score: 25,
      perEngine: { chatgpt: 28, perplexity: 26, google_aio: 20 },
    },
    {
      date: '2026-02-28',
      score: 26,
      perEngine: { chatgpt: 29, perplexity: 27, google_aio: 22 },
    },
    {
      date: '2026-03-07',
      score: 28,
      perEngine: { chatgpt: 31, perplexity: 29, google_aio: 23 },
      annotation: 'Task live: Add FAQ markup to /pricing',
    },
    {
      date: '2026-03-14',
      score: 29,
      perEngine: { chatgpt: 33, perplexity: 30, google_aio: 24 },
    },
    {
      date: '2026-03-21',
      score: 30,
      perEngine: { chatgpt: 34, perplexity: 31, google_aio: 25 },
    },
    {
      date: '2026-03-28',
      score: 31,
      perEngine: { chatgpt: 35, perplexity: 33, google_aio: 25 },
    },
  ],
  lastScannedAt: '2h ago',
}
